import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { LANGS, TL } from "../i18n";
import { Card, Button, Input, Select, Badge } from "../components/ui";
import { Users, Building2, Globe, UserPlus, Trash2, Search, Link2, ShieldCheck } from "lucide-react";

const langLabel = (code) => (LANGS.find((l) => l.code === code)?.label || code);

export default function MyTeam() {
  const { delegatedKeys = [] } = useApp();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [agents, setAgents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [runners, setRunners] = useState([]);
  const [rq, setRq] = useState("");
  const [pick, setPick] = useState({ agent_id: "", runner_id: "", language: "" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const loadAssignments = useCallback(() => {
    client.get("/manager/assignments").then((r) => setAssignments(r.data.assignments || [])).catch(() => {});
  }, []);

  useEffect(() => {
    client.get("/manager/me").then((r) => setMe(r.data)).catch((e) => {
      if (e?.response?.status === 403) { navigate("/app", { replace: true }); return; }
      setErr(apiErr(e));
    });
    client.get("/manager/agents").then((r) => setAgents(r.data.agents || [])).catch(() => {});
    loadAssignments();
  }, [loadAssignments, navigate]);

  const searchRunners = useCallback(() => {
    client.get(`/manager/runners?q=${encodeURIComponent(rq)}`).then((r) => setRunners(r.data.runners || [])).catch(() => {});
  }, [rq]);
  useEffect(() => { const t = setTimeout(searchRunners, 300); return () => clearTimeout(t); }, [searchRunners]);

  const assign = async () => {
    setErr(""); setMsg("");
    if (!pick.agent_id || !pick.runner_id) { setErr("Pick both an agent and a runner."); return; }
    try {
      await client.post("/manager/assignments", pick);
      setMsg("Agent assigned."); setPick({ agent_id: "", runner_id: "", language: "" }); loadAssignments();
    } catch (e) { setErr(apiErr(e)); }
  };
  const unassign = async (id) => {
    try { await client.delete(`/manager/assignments/${id}`); loadAssignments(); } catch { /* noop */ }
  };

  const isRunnerMgmt = me?.level === "runner_mgmt";

  return (
    <div className="space-y-6 fadeup" data-testid="my-team-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><Users size={24} /> {TL("My Team")}</h1>
        {me && <Badge color="info">{me.level === "high_level" ? "High-level manager" : "Runner-management manager"}</Badge>}
        {me?.offices?.map((o) => <span key={o.id} className="flex items-center gap-1 rounded-full bg-black/10 px-2 py-0.5 text-xs dark:bg-white/10"><Building2 size={11} />{o.name}</span>)}
      </div>
      <p className="opacity-60 text-sm -mt-2">{TL("You only see your own office(s) and the people connected to you — nothing else across the network.")}</p>

      {err && <Card className="border-[#DA1E28]/40 text-sm text-[#DA1E28]" data-testid="myteam-err">{err}</Card>}
      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="myteam-msg">{msg}</Card>}

      {me?.level === "high_level" && (
        <Card className="space-y-2" data-testid="highlevel-panels">
          <div className="flex items-center gap-2 font-head"><ShieldCheck size={18} className="text-[var(--primary)]" /> {TL("Delegated panels")}</div>
          {delegatedKeys.length ? (
            <div className="flex flex-wrap gap-1.5 text-sm">
              {delegatedKeys.map((k) => <span key={k} className="rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[var(--primary)] capitalize">{k.replace(/_/g, " ")}</span>)}
            </div>
          ) : <p className="text-sm opacity-60">{TL("No admin panels are currently delegated to you. When they are, they'll appear in your sidebar and the alert console.")}</p>}
        </Card>
      )}

      {isRunnerMgmt && (
        <>
          <Card className="space-y-3" data-testid="assign-tool">
            <div className="flex items-center gap-2 font-head"><Link2 size={18} className="text-[var(--primary)]" /> {TL("Assign an agent to a runner")}</div>
            <div className="grid gap-3 md:grid-cols-3">
              <Select label={TL("Agent (your office)")} value={pick.agent_id} onChange={(e) => setPick({ ...pick, agent_id: e.target.value })} data-testid="assign-agent">
                <option value="">{TL("Choose agent…")}</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.role}{a.languages?.length ? ` (${a.languages.map(langLabel).join("/")})` : ""}</option>)}
              </Select>
              <div>
                <label className="mb-1 block text-xs opacity-60">{TL("Runner")}</label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
                  <input value={rq} onChange={(e) => setRq(e.target.value)} placeholder={TL("Search runners…")} className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 py-2 ps-8 pe-2 text-sm" data-testid="assign-runner-search" />
                </div>
                <Select className="mt-1" value={pick.runner_id} onChange={(e) => setPick({ ...pick, runner_id: e.target.value })} data-testid="assign-runner">
                  <option value="">{TL("Choose runner…")}</option>
                  {runners.map((r) => <option key={r.id} value={r.id}>{r.name}{r.country ? ` — ${r.country}` : ""}</option>)}
                </Select>
              </div>
              <Select label={TL("Language (optional)")} value={pick.language} onChange={(e) => setPick({ ...pick, language: e.target.value })} data-testid="assign-language">
                <option value="">—</option>
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </Select>
            </div>
            <div><Button onClick={assign} data-testid="assign-submit"><UserPlus size={15} /> {TL("Assign")}</Button></div>
          </Card>

          <Card className="space-y-2" data-testid="my-agents">
            <div className="font-head">My office agents ({agents.length})</div>
            <div className="flex flex-wrap gap-1.5 text-sm">
              {agents.length === 0 && <span className="opacity-50">{TL("No agents share your office yet — ask Prime to place agents in your office.")}</span>}
              {agents.map((a) => <span key={a.id} className="rounded-full border border-black/15 px-2 py-0.5 dark:border-white/15">{a.name}</span>)}
            </div>
          </Card>
        </>
      )}

      <Card className="space-y-2" data-testid="assignments-list">
        <div className="font-head">Current assignments ({assignments.length})</div>
        {assignments.length === 0 ? <p className="text-sm opacity-60">{TL("No assignments yet.")}</p> : (
          <ul className="divide-y divide-black/5 dark:divide-white/5">
            {assignments.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`assignment-${a.id}`}>
                <span className="font-medium">{a.agent_name}</span>
                <Link2 size={13} className="opacity-40" />
                <span>{a.runner_name}</span>
                {a.language && <span className="flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-xs text-[var(--primary)]"><Globe size={11} />{langLabel(a.language)}</span>}
                <button onClick={() => unassign(a.id)} data-testid={`unassign-${a.id}`} className="ms-auto text-[#DA1E28] hover:opacity-70"><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
